import { applySnapshot, flow, Instance, types, SnapshotIn } from "mobx-state-tree";
import { CoachLeadRowModel, ItemMyLeadsModel, LeadClientRowModel, MyLeadsParamsModel } from "../models/MyLeads";
import { ContextModel, SearchModel } from "../models/Common";
import { api } from "../service/api";
import { IMyLeadsCSVData, IMyLeadsParam } from "../constants/interfaces";
import { serviceCoach } from "../utils/env";
import { formatDate } from "../utils/date";

const MyLeadsStore = types
  .model({
    items: types.maybeNull(types.array(ItemMyLeadsModel)),
    rows: types.optional(types.array(types.union(LeadClientRowModel, CoachLeadRowModel)), []),
    searchItems: types.optional(types.array(SearchModel), []),
    emailSearchItems: types.optional(types.array(SearchModel), []),
    context: types.maybeNull(ContextModel),
    isLoading: types.optional(types.boolean, true),
    params: types.optional(MyLeadsParamsModel, {}),
  })
  .actions((self) => ({
    buildUrl(params: IMyLeadsParam, url: string) {
      const urlDefault = `${serviceCoach}${url}`;
      const queryParams = new URLSearchParams();

      Object.keys(params).forEach((key) => {
        const value = params[key as keyof IMyLeadsParam];
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });

      return `${urlDefault}?${queryParams.toString()}`;
    },
  }))
  .actions((self) => ({
    fetch: flow(function* (params: IMyLeadsParam, isLeadClient: boolean = false) {
      try {
        const url = self.buildUrl(params, "/api/v1/my-leads");
        const { data } = yield api.get(url);
        const { context, items } = data;
        let rowsAux: Array<SnapshotIn<typeof LeadClientRowModel> | SnapshotIn<typeof CoachLeadRowModel>> = [];
        self.isLoading = true;

        if (items?.length && items.length > 0) {
          items?.forEach((item: Instance<typeof ItemMyLeadsModel>, index: number) => {
            const base = {
              id: item.id ?? index,
              date: item?.date ? formatDate(item.date) : "-",
              firstName: item.firstName ?? "-",
              lastName: item.lastName ?? "-",
              phone: item.phone ?? "-",
              email: item.email ?? "-",
            };

            const row = isLeadClient
              ? {
                  type: "leadClient" as const,
                  ...base,
                  source: item.source ?? "-",
                }
              : {
                  type: "coachLead" as const,
                  ...base,
                  clientNotClient: item.clientNotClient ? "Client" : "Not a Client",
                };

            rowsAux = [...rowsAux, row];
          });
        }

        applySnapshot(self, {
          ...self,
          items,
          rows: rowsAux,
          context,
          isLoading: false,
          params,
        });
      } catch (error) {
        console.log("Error fetch My Leads:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchCSVData: flow(function* (params: IMyLeadsParam, isLeadClient: boolean = false) {
      try {
        const url = self.buildUrl(params, "/api/v1/my-leads");
        const { data } = yield api.get(url);
        const { items } = data;
        let csvData: IMyLeadsCSVData[] | [] = [];
        self.isLoading = true;

        if (items?.length && items.length > 0) {
          items?.forEach((item: Instance<typeof ItemMyLeadsModel>) => {
            const base = {
              date: item?.date ? formatDate(item.date) : "-",
              firstName: item.firstName ?? "-",
              lastName: item.lastName ?? "-",
              phone: item.phone ?? "-",
              email: item.email ?? "-",
            };

            const row = isLeadClient
              ? {
                  ...base,
                  source: item.source ?? "-",
                }
              : {
                  ...base,
                  clientNotClient: "clientNotClient" in item ? (item.clientNotClient ? "Client" : "Not a Client") : "",
                };

            csvData = [...csvData, row];
          });
        }
        return csvData;
      } catch (error) {
        console.log("Error fetch My Leads:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchSearch: flow(function* (params: IMyLeadsParam) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/my-leads/search");
        const { data } = yield api.get(url);

        applySnapshot(self, {
          ...self,
          searchItems: data,
        });
      } catch (error) {
        console.log("Error fetchSearch My Leads:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchEmailSearch: flow(function* (params: IMyLeadsParam) {
      try {
        self.isLoading = true;
        const url = self.buildUrl(params, "/api/v1/my-leads/email-search");
        const { data } = yield api.get(url);

        applySnapshot(self, {
          ...self,
          emailSearchItems: data,
        });
      } catch (error) {
        console.log("Error fetchEmailSearch My Leads:", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    clear: function* () {
      applySnapshot(self, {
        ...self,
        items: [],
        rows: [],
        context: undefined,
      });
    },
  }));

export const myLeadsStore = MyLeadsStore.create();
